import React, { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "@/services/hooks"
import { Container } from "reactstrap"
import SimpleHeader from "@/components/Headers/SimpleHeader"
import DisplayPendingChangesView from "./DisplayPendingChangesView"
import {
  getPendingChanges,
  approvePendingChange,
  approveChangeset,
  rejectPendingChange,
  clearChangesMessage,
} from "@/services/pendingChangesServices/pendingChangesActions"
import { getAllProperties } from "@/services/productServices/productActions"

const PAGE_SIZE = 8

/** A pending-change row (GET /api/pending-changes), reduced to what this container filters on. */
type PendingChangeRow = { _id: string; status: string; entityType?: string }

const DisplayPendingChangesContainer = () => {
  const dispatch = useAppDispatch()
  const { pendingChanges, message } = useAppSelector((s) => s.pendingChanges)
  const allProperties = useAppSelector((s) => s.products?.properties || [])
  const currentSiteId = useAppSelector((s) => s.sites?.site?._id || null)

  const [activeTab, setActiveTab] = useState("all")
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [pendingPage, setPendingPage] = useState(1)
  const [reviewedPage, setReviewedPage] = useState(1)

  useEffect(() => {
    dispatch(getPendingChanges())
    dispatch(getAllProperties())
  }, [dispatch])

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => dispatch(clearChangesMessage()), 3000)
      return () => clearTimeout(t)
    }
  }, [message, dispatch])

  const filtered = (items: PendingChangeRow[]) => {
    if (activeTab === "all") return items
    return items.filter((c) => c.entityType === activeTab)
  }

  const pendingItems = filtered(pendingChanges.filter((c) => c.status === "pending"))
  const reviewedItems = filtered(
    pendingChanges.filter((c) => c.status === "approved" || c.status === "rejected")
  )

  const onToggleExpand = (id: string) => setExpandedId((prev) => (prev === id ? null : id))

  const onApprove = (id: string) => {
    dispatch(approvePendingChange(id))
    setExpandedId(null)
  }

  const onApproveChangeset = (name: string) => {
    dispatch(approveChangeset(name))
    setExpandedId(null)
  }

  const onReject = (id: string) => {
    dispatch(rejectPendingChange(id))
    setExpandedId(null)
  }

  const onTabChange = (tab: string) => {
    setActiveTab(tab)
    setPendingPage(1)
    setReviewedPage(1)
  }

  const paginatedPending = pendingItems.slice(
    (pendingPage - 1) * PAGE_SIZE,
    pendingPage * PAGE_SIZE
  )
  const paginatedReviewed = reviewedItems.slice(
    (reviewedPage - 1) * PAGE_SIZE,
    reviewedPage * PAGE_SIZE
  )

  return (
    <>
      <SimpleHeader name="Pending Changes" parentName="Publishing" />
      <Container className="mt-4" fluid>
        <DisplayPendingChangesView
          pendingItems={paginatedPending}
          reviewedItems={paginatedReviewed}
          allProperties={allProperties}
          currentSiteId={currentSiteId}
          totalPending={pendingItems.length}
          totalReviewed={reviewedItems.length}
          activeTab={activeTab}
          onTabChange={onTabChange}
          expandedId={expandedId}
          onToggleExpand={onToggleExpand}
          onApprove={onApprove}
          onApproveChangeset={onApproveChangeset}
          onReject={onReject}
          pendingPage={pendingPage}
          setPendingPage={setPendingPage}
          reviewedPage={reviewedPage}
          setReviewedPage={setReviewedPage}
          PAGE_SIZE={PAGE_SIZE}
          message={message}
        />
      </Container>
    </>
  )
}

export default DisplayPendingChangesContainer
